import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter } from "./AppOverlay";
import {
  archiveAdminData,
  deleteAdminData,
  restoreAdminData,
} from "../../helpers/backend_helper";
import { ErrorNotify, SuccessNotify } from "../../shared/Alert";

const archivedStatus = (dataType, record) => {
  if (dataType === "report-template")
    return (
      String(record?.status || record?.lifecycleStatus).toLowerCase() ===
      "archived"
    );
  if (dataType === "import-definition")
    return ["inactive", "archived"].includes(
      String(record?.status).toLowerCase()
    );
  if (
    [
      "checklist",
      "checklist-area",
      "checklist-topic",
      "standard",
      "service-code",
      "mandatory-service-code",
    ].includes(dataType)
  )
    return record?.status === 2;
  return record?.status === 0;
};

const recordName = (record) =>
  record?.name || record?.fullName || record?.title || "this record";

export const useAdminDataLifecycle = (onChanged) => {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [referenceResult, setReferenceResult] = useState(null);

  const close = useCallback(() => {
    if (busy) return;
    setRequest(null);
    setReferenceResult(null);
  }, [busy]);

  const ask = useCallback((action, dataType, record) => {
    setReferenceResult(null);
    setRequest({ action, dataType, record });
  }, []);

  const actions = useCallback(
    (dataType, record) => {
      const archived = archivedStatus(dataType, record);
      if (dataType === "user") return [{
        label: archived ? "Restore firm access" : "Remove access to this firm",
        icon: <i className={archived ? "bx bx-reset" : "bx bx-user-x"} />,
        onSelect: () => ask(archived ? "restore" : "archive", dataType, record),
      }];
      return [
        archived
          ? {
              label: "Restore",
              icon: <i className="bx bx-reset" />,
              onSelect: () => ask("restore", dataType, record),
            }
          : {
              label: "Archive",
              icon: <i className="bx bx-archive-in" />,
              onSelect: () => ask("archive", dataType, record),
            },
        {
          label: "Delete",
          icon: <i className="bx bx-trash" />,
          className: "is-danger",
          separatorBefore: true,
          onSelect: () => ask("delete", dataType, record),
        },
      ];
    },
    [ask]
  );

  const execute = useCallback(
    async (forcedAction) => {
      if (!request) return;
      const action = forcedAction || request.action;
      const operation =
        action === "delete"
          ? deleteAdminData
          : action === "archive"
          ? archiveAdminData
          : restoreAdminData;
      setBusy(true);
      try {
        const response = await operation(request.dataType, request.record.id, request.dataType === "user" ? request.record.firmId : undefined);
        const result = response?.data;
        if (result?.code === "HasReferences") {
          setReferenceResult(result);
          return;
        }
        if (!response?.status) {
          ErrorNotify(response?.message || `Unable to ${action} the record.`);
          return;
        }
        SuccessNotify(response?.message || `Record ${action}d successfully.`);
        setRequest(null);
        setReferenceResult(null);
        await onChanged?.();
      } catch (error) {
        console.error(error);
        ErrorNotify(`Unable to ${action} the record.`);
      } finally {
        setBusy(false);
      }
    },
    [onChanged, request]
  );

  const dialog = useMemo(() => {
    if (!request) return null;
    const name = recordName(request.record);
    const offerArchive = Boolean(referenceResult);
    const actionLabel = request.dataType === "user" ? (request.action === "archive" ? "Remove firm access for" : "Restore firm access for") :
      request.action === "delete"
        ? "Delete"
        : request.action === "archive"
        ? "Archive"
        : "Restore";
    return (
      <Modal
        isOpen
        toggle={close}
        centered
        aria-label={`${actionLabel} ${name}`}
      >
        <ModalBody>
          <h5 className="mb-2">
            {offerArchive ? "This record is in use" : `${actionLabel} ${name}?`}
          </h5>
          {offerArchive ? (
            <>
              <p>{referenceResult.message}</p>
              {referenceResult.references?.length > 0 && (
                <ul className="mb-0">
                  {referenceResult.references.map((item) => (
                    <li key={item.dataType}>
                      {item.dataType}: {item.count}
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : request.dataType === "user" ? (
            <p className="mb-0">This changes <strong>{name}</strong>'s access to this firm only. Their identity, other firm memberships, and historical assignments are preserved.</p>
          ) : request.action === "delete" ? (
            <p className="mb-0">
              Permanently delete <strong>{name}</strong>? The server will delete
              it only if it has no references. This action cannot be undone.
            </p>
          ) : request.action === "archive" ? (
            <p className="mb-0">
              Archive <strong>{name}</strong>? It will no longer be available
              for new selections, while historical records remain unchanged.
            </p>
          ) : (
            <p className="mb-0">
              Restore <strong>{name}</strong> and make it available for new
              selections?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={close} disabled={busy}>
            Cancel
          </Button>
          {offerArchive ? (
            <Button
              color="primary"
              onClick={() => execute("archive")}
              disabled={busy}
            >
              {busy ? "Archiving..." : "Archive"}
            </Button>
          ) : (
            <Button
              color={request.action === "delete" ? "danger" : "primary"}
              onClick={() => execute()}
              disabled={busy}
            >
              {busy ? "Working..." : actionLabel}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }, [busy, close, execute, referenceResult, request]);

  return { actions, dialog };
};

export default useAdminDataLifecycle;
